import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";
import type { RecipeApiItem, RecipeStep } from "@recipevault/shared";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog.js";
import { Button } from "../../ui/button.js";
import { RecipePhotoImg } from "../recipes/RecipePhotoImg.js";
import { presignPhoto } from "../recipes/api.js";
import { sync } from "../../sync/SyncService.js";
import {
  validateInputFile,
  resizeImage,
  uploadPair,
  isSafari,
  ALLOWED_INPUT_TYPES,
} from "../../shared/photos.js";

// rv: spec §6.4.1 calls for 1/2/3/4+ adaptive grid layouts + pinch-zoom per photo.
// v1 uses a simple responsive grid (grid-cols-2 sm:grid-cols-3). Adaptive layout
// and pinch-zoom are deferred polish items — mark with rv: when revisiting.

type Props = {
  householdId: string;
  recipe: RecipeApiItem;
  step: RecipeStep;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type UploadPhase = "idle" | "validating" | "resizing" | "presigning" | "uploading" | "confirming";

/**
 * Modal for managing step photos in cooking mode.
 *
 * - Shows step.imageKeys in a responsive grid.
 * - "+ Add photo" triggers presign → resize → upload → confirm flow with stepId.
 * - Long-press (or tap-hold) a photo → confirm dialog → delete via deletePhotoOptimistic.
 *
 * rv: Adaptive 1/2/3/4+ column layouts and pinch-zoom (spec §6.4.1) are deferred.
 * rv: Simple pager / enlarged single-photo view deferred — tapping a photo currently
 *     does nothing (grid-only for v1).
 */
export function StepPhotoModal({ householdId, recipe, step, open, onOpenChange }: Props) {
  const [phase, setPhase] = useState<UploadPhase>("idle");
  const [pendingDeleteKey, setPendingDeleteKey] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const uploading = phase !== "idle";

  async function handleFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    const file = files[0] as File;

    setPhase("validating");
    const validation = validateInputFile(file, { isSafari: isSafari() });
    if (!validation.ok) {
      toast.error(validation.message);
      setPhase("idle");
      return;
    }

    try {
      setPhase("resizing");
      const blobs = await resizeImage(file);

      setPhase("presigning");
      const presigned = await presignPhoto(recipe.id);

      setPhase("uploading");
      await uploadPair(blobs, { thumbUrl: presigned.thumbUrl, fullUrl: presigned.fullUrl });

      setPhase("confirming");
      await sync.confirmPhotoOptimistic(recipe.id, {
        photoKey: presigned.photoKey,
        version: recipe.version,
        stepId: step.id,
      });

      toast.success("Photo added");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Photo upload failed");
    } finally {
      setPhase("idle");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDelete(photoKey: string): Promise<void> {
    try {
      await sync.deletePhotoOptimistic(recipe.id, photoKey, recipe.version);
      toast.success("Photo deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete photo");
    } finally {
      setPendingDeleteKey(null);
    }
  }

  function startLongPress(photoKey: string): void {
    longPressTimer.current = setTimeout(() => {
      setPendingDeleteKey(photoKey);
    }, 500);
  }

  function cancelLongPress(): void {
    if (longPressTimer.current != null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Step Photos</DialogTitle>
        </DialogHeader>

        {/* Photo grid — rv: adaptive 1/2/3/4+ layout deferred (v1 is simple 2-col grid) */}
        {step.imageKeys.length > 0 && (
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2" aria-label="Step photos">
            {step.imageKeys.map((photoKey) => (
              <li key={photoKey} className="relative group">
                <RecipePhotoImg
                  householdId={householdId}
                  recipeId={recipe.id}
                  photoKey={photoKey}
                  size="thumb"
                  alt="Step photo"
                  className="w-full aspect-square object-cover rounded-md"
                />
                {/* Long-press affordance: tap-and-hold on touch; right-click on desktop */}
                <button
                  type="button"
                  className="absolute inset-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Delete photo ${photoKey}`}
                  onPointerDown={() => startLongPress(photoKey)}
                  onPointerUp={cancelLongPress}
                  onPointerLeave={cancelLongPress}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setPendingDeleteKey(photoKey);
                  }}
                />
              </li>
            ))}
          </ul>
        )}

        {step.imageKeys.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">
            No photos yet. Tap below to add one.
          </p>
        )}

        {/* + Add photo */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept={Array.from(ALLOWED_INPUT_TYPES).join(",")}
            aria-label="Choose photo to upload"
            className="hidden"
            onChange={(e) => {
              void handleFiles(e.target.files);
            }}
          />
          <Button
            type="button"
            variant="outline"
            className="w-full gap-2"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <Camera className="size-4" aria-hidden="true" />
            {uploading ? `Uploading… (${phase})` : "+ Add photo"}
          </Button>
        </div>

        {/* Confirm delete sheet */}
        {pendingDeleteKey != null && (
          <div
            role="alertdialog"
            aria-label="Delete photo?"
            className="mt-2 rounded-md border border-destructive/40 bg-destructive/10 p-4 flex flex-col gap-3"
          >
            <p className="text-sm">Delete this step photo? This cannot be undone.</p>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPendingDeleteKey(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => {
                  void handleDelete(pendingDeleteKey);
                }}
              >
                <Trash2 className="size-4 mr-1" aria-hidden="true" />
                Delete
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
